import React, { useEffect, useId, useState } from 'react';
import {
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  IconButton,
  FormHelperText,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PropTypes from 'prop-types';
import { deepClone } from '@tenant/utility/helper/Utils';
import DockerImageSelect from '@tenant/core/App/Select/DockerImageSelect';

const generateRandomID = () => {
  return 'id-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
};

const DockerImageCollapseCard = ({
  handleChange = () => {},
  initialValues,
  header,
  timeSubmitted,
  appId,
}) => {
  // example value: { id: '1', Name: 'newname', ImageNameItem: null, ImageName: '' },
  const [environments, setEnvironments] = useState(deepClone(initialValues));
  const uniqueId = useId();

  const checkNameValidation = (name) => {
    return name.length < 255;
  };

  const checkDockerImageValidation = (dockerImage) => {
    return !!dockerImage;
  };

  useEffect(() => {
    if (timeSubmitted > 0) {
      setEnvironments((prevEnvironments) =>
        prevEnvironments.map((env) => ({
          ...env,
          isTouchedImageNameItem: true,
        })),
      );
    }
  }, [timeSubmitted]);

  useEffect(() => {
    // handle change include params: (value, isValid)
    handleChange(
      environments.map((e) => ({
        id: e.id,
        Name: e.Name,
        ImageName: e.ImageNameItem?.Name,
        ImageNameItem: e.ImageNameItem,
      })),
      !environments.some(
        (e) =>
          !checkNameValidation(e.Name) ||
          !checkDockerImageValidation(e.ImageNameItem),
      ),
    );
  }, [environments]);

  const setFieldValue = (index, field, value) => {
    setEnvironments((prevEnvironments) =>
      prevEnvironments.map((env, i) =>
        i === index ? { ...env, [field]: value } : env,
      ),
    );
  };

  const deleteEnv = (env) => {
    setEnvironments((s) => s.filter((e) => e.id !== env.id));
  };

  const addNewRow = () => {
    setEnvironments((s) => [
      ...s,
      {
        id: 'new-item-' + generateRandomID(),
        Name: '',
        ImageName: null,
      },
    ]);
  };

  return (
    <Card
      variant='outlined'
      sx={{
        mb: 3,
        boxShadow: 'none',
        borderColor: 'transparent',
        borderRadius: 0,
        '& .MuiCardContent-root': {
          p: 0,
        },
        '& .MuiCardActions-root': {
          p: 0,
        },
      }}
    >
      <CardContent>
        <Box display={'flex'} alignItems={'center'}>
          {header}
        </Box>

        {environments?.map((env, idx) => (
          <Grid
            mt={4}
            mb={4}
            alignItems='start'
            key={env.id}
            container
            spacing={5}
          >
            <Grid item xs={4}>
              <TextField
                fullWidth
                value={env.Name}
                label='Container App Name *'
                inputProps={{ maxLength: 255 }}
                placeholder='E.g. containername'
                error={
                  env.isTouchedImageNameItem && !checkNameValidation(env.Name)
                }
                helperText={
                  env.isTouchedImageNameItem && !checkNameValidation(env.Name)
                    ? 'Name is invalid'
                    : ''
                }
                onChange={(e) => {
                  setFieldValue(idx, 'Name', e.target.value);
                  setFieldValue(idx, 'isTouchedImageNameItem', true);
                }}
              />
            </Grid>
            <Grid item xs={7}>
              <DockerImageSelect
                fullWidth
                isMultiple={false}
                value={env.ImageNameItem || null}
                label='Container Image *'
                queryKey={uniqueId}
                params={{ AppId: appId }}
                error={
                  env.isTouchedImageNameItem &&
                  !checkDockerImageValidation(env.ImageNameItem)
                }
                helperText={
                  env.isTouchedImageNameItem &&
                  !checkDockerImageValidation(env.ImageNameItem)
                    ? 'Image Name is required'
                    : ''
                }
                onChange={(e, value) => {
                  setFieldValue(idx, 'ImageNameItem', value);
                  setFieldValue(idx, 'isTouchedImageNameItem', true);
                }}
              />
            </Grid>
            <Grid item xs={1}>
              {idx !== 0 && (
                <IconButton aria-label='delete' onClick={() => deleteEnv(env)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
        <Button sx={{ mt: 3 }} onClick={addNewRow}>
          {' '}
          Add New Row{' '}
        </Button>
      </CardContent>
    </Card>
  );
};

export default React.memo(DockerImageCollapseCard);

DockerImageCollapseCard.propTypes = {
  handleChange: PropTypes.func,
  initialValues: PropTypes.array,
  header: PropTypes.node,
  timeSubmitted: PropTypes.number,
  appId: PropTypes.number,
};
